'use client'

import { NewDropdown } from '@/components/NewDropdown'
import { NewDropdownOption } from '@/components/NewDropdown/types'
import { type FilterSelectionMode } from '@/lib/utils/filters'
import { difference } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Container } from './Container'

export type ComboboxSearchValue<T extends FilterSelectionMode> = T extends 'single'
  ? string
  : string[]

type ComboboxSearchProps<T extends FilterSelectionMode = 'multiple'> = {
  selectionMode?: T
  name: string
  title: string
  triggerText?: string
  searchPlaceholder?: string
  emptyMessage?: string
  options: NewDropdownOption[]
  selectedOptions: ComboboxSearchValue<T>
  setSelectedOptions: React.Dispatch<React.SetStateAction<ComboboxSearchValue<T>>>
  resetValue?: ComboboxSearchValue<T>
  hideTitle?: boolean
  /** Drops the section's leading separator (see `Container`). */
  hideSeparator?: boolean
  hideBadges?: boolean
  loading?: boolean
  /** Scrolls long option labels sideways instead of truncating them. */
  hasHorizontalScroll?: boolean
  /** Renders at most this many matching options; the rest are reached by searching. */
  maxVisibleOptions?: number
  /** Splits the search on this character into terms that must all match. */
  searchTermSeparator?: string
  /** Notified as the search box changes, for options fetched from the server as the user types. */
  onSearchChange?: (search: string) => void
  /** A server search for the current text is still outstanding; an empty list shows as loading. */
  isSearching?: boolean
  /** The list reached its end; append the next page of server matches. */
  onEndReached?: () => void
  /** The server holds more matches beyond the options given. */
  hasMoreOptions?: boolean
  /** The next page of matches is being fetched. */
  isLoadingMore?: boolean
  /**
   * Called instead of writing the default value back, for a consumer whose setter cannot take an empty
   * write — a single-select panel that treats an emptying click as a no-op would otherwise swallow a
   * reset to no selection at all.
   */
  onReset?: () => void
  /** Rendered under the dropdown, inside the section (e.g. a link to manage the options). */
  footer?: React.ReactNode
  /** Locks the selection: the dropdown can't be opened and Reset is hidden. */
  disabled?: boolean
}

export function ComboboxSearch<T extends FilterSelectionMode = 'multiple'>(
  props: ComboboxSearchProps<T>,
) {
  const { t } = useTranslation('common')

  const defaultValue =
    props.resetValue ?? ((props.selectionMode === 'single' ? '' : []) as ComboboxSearchValue<T>)

  const hasOptionSelected = Array.isArray(props.selectedOptions)
    ? props.selectedOptions.length > 0
    : Boolean(props.selectedOptions)

  const isDifferentFromDefaultValue = Array.isArray(props.selectedOptions)
    ? difference(props.selectedOptions, props.resetValue ?? []).length > 0 ||
      difference(props.resetValue ?? [], props.selectedOptions).length > 0
    : props.selectedOptions != props.resetValue

  const handleReset = () => {
    if (props.onReset) return props.onReset()

    props.setSelectedOptions(defaultValue)
  }

  return (
    <Container
      isResetVisible={
        !props.disabled && (props.resetValue ? isDifferentFromDefaultValue : hasOptionSelected)
      }
      onReset={handleReset}
      hasOptionsSelected={hasOptionSelected}
      title={props.hideTitle ? '' : props.title}
      hideSeparator={props.hideSeparator}
    >
      <NewDropdown
        options={props.options}
        value={props.selectedOptions}
        onChange={(next) => props.setSelectedOptions(next as ComboboxSearchValue<T>)}
        selectionMode={props.selectionMode ?? 'multiple'}
        searchable={true}
        placeholder={props.triggerText ?? t('comboboxSearch.triggerText')}
        loading={props.loading}
        disabled={props.disabled}
        hasHorizontalScroll={props.hasHorizontalScroll}
        maxVisibleOptions={props.maxVisibleOptions}
        searchTermSeparator={props.searchTermSeparator}
        onSearchChange={props.onSearchChange}
        isSearching={props.isSearching}
        onEndReached={props.onEndReached}
        hasMoreOptions={props.hasMoreOptions}
        isLoadingMore={props.isLoadingMore}
        text={{
          searchPlaceholder: props.searchPlaceholder || t('comboboxSearch.searchPlaceholder'),
          emptyResult: props.emptyMessage || t('comboboxSearch.emptyResult'),
        }}
        style={{
          stackClassName: 'w-full',
        }}
      />
      {props.footer}
    </Container>
  )
}
